import { AxiosError } from 'axios';
import { isLoading } from '@fusion/utils';
import { useAuthCheckV3 } from '../hooks';
import { isInitialised, initialiseFlags, featureEnabled } from '../constants';
import { userV3Ctx } from '../contexts';
import { User } from '@fusion/auth';

type UserProviderProps = {
  LoadingComponent: React.ElementType;
  /** Shown in place of the app when the user can't be loaded and the API gives an error code for it */
  ErrorComponent?: React.ElementType<{ code: string }>;
  children: React.ReactNode;
};

const getErrorCode = (error: unknown): string | undefined => {
  const code = (error as AxiosError | null)?.response?.data?.data?.code;

  return typeof code === 'string' ? code : undefined;
};

export const UserV3Provider: React.ElementType<UserProviderProps> = ({
  LoadingComponent,
  ErrorComponent,
  children,
}) => {
  const { data: user, error, ...authCheckQuery } = useAuthCheckV3();

  if (isLoading(authCheckQuery)) {
    return <LoadingComponent />;
  }

  const errorCode = getErrorCode(error);

  if (!user && errorCode && ErrorComponent) {
    return <ErrorComponent code={errorCode} />;
  }

  if (!isInitialised()) {
    initialiseFlags(user && user.profile && user.experimentalFeatures ? user.experimentalFeatures : {});
  }

  return (
    <userV3Ctx.Provider
      value={{
        data: user as User,
        featureEnabled,
        isUniversityUser: (user && ['university', 'university-admin'].includes(user.userGroup.alias)) || false,
      }}
    >
      {children}
    </userV3Ctx.Provider>
  );
};
